import { trackFullStoryEvent } from 'helpers/fullstory';

import type {
  MarkContextKind,
  MarkEntryPoint,
  MarkPanelCloseMethod,
  MarkSurfaceId,
} from '../mark-panel/mark-panel.types';
import type { MarkPromptSource } from '../ai-chat.types';

import {
  MARK_PANEL_EVENT,
  type MarkChatAnalyticsSurface,
  type MarkPanelResponseErrorType,
  type MarkPanelResponseRequestType,
} from './mark-panel-analytics.constants';

interface MarkContextIconClickedProps {
  surface: MarkSurfaceId;
  contextKind: MarkContextKind;
  contextSection: string;
  contextItemId?: string;
}

export const trackMarkContextIconClicked = ({
  surface,
  contextKind,
  contextSection,
  contextItemId,
}: MarkContextIconClickedProps): void => {
  trackFullStoryEvent(MARK_PANEL_EVENT.contextIconClicked, {
    surface,
    contextKind,
    contextSection,
    ...(contextItemId && { contextItemId }),
  });
};

interface MarkFloatingButtonClickedProps {
  surface: MarkSurfaceId;
}

export const trackMarkFloatingButtonClicked = ({
  surface,
}: MarkFloatingButtonClickedProps): void => {
  trackFullStoryEvent(MARK_PANEL_EVENT.floatingButtonClicked, { surface });
};

interface MarkPanelOpenedProps {
  surface: MarkSurfaceId;
  entryPoint: MarkEntryPoint;
  contextKind: MarkContextKind;
  contextSection: string;
}

export const trackMarkPanelOpened = ({
  surface,
  entryPoint,
  contextKind,
  contextSection,
}: MarkPanelOpenedProps): void => {
  trackFullStoryEvent(MARK_PANEL_EVENT.panelOpened, {
    surface,
    entryPoint,
    contextKind,
    contextSection,
  });
};

interface MarkPanelClosedProps {
  surface: MarkSurfaceId;
  closeMethod: MarkPanelCloseMethod;
  hasConversation: boolean;
}

export const trackMarkPanelClosed = ({
  surface,
  closeMethod,
  hasConversation,
}: MarkPanelClosedProps): void => {
  trackFullStoryEvent(MARK_PANEL_EVENT.panelClosed, {
    surface,
    closeMethod,
    hasConversation,
  });
};

interface MarkChatStartedProps {
  surface: MarkChatAnalyticsSurface;
  promptType: MarkPromptSource;
  contextKind?: MarkContextKind;
  contextSection?: string;
}

export const trackMarkChatStarted = ({
  surface,
  promptType,
  contextKind,
  contextSection,
}: MarkChatStartedProps): void => {
  trackFullStoryEvent(MARK_PANEL_EVENT.chatStarted, {
    surface,
    promptType,
    ...(contextKind && { contextKind }),
    ...(contextSection && { contextSection }),
  });
};

interface MarkResponseReceivedProps {
  surface: MarkChatAnalyticsSurface;
  requestType: MarkPanelResponseRequestType;
}

export const trackMarkResponseReceived = ({
  surface,
  requestType,
}: MarkResponseReceivedProps): void => {
  trackFullStoryEvent(MARK_PANEL_EVENT.responseReceived, {
    surface,
    requestType,
  });
};

interface MarkResponseFailedProps {
  surface: MarkChatAnalyticsSurface;
  requestType: MarkPanelResponseRequestType;
  errorType: MarkPanelResponseErrorType;
}

export const trackMarkResponseFailed = ({
  surface,
  requestType,
  errorType,
}: MarkResponseFailedProps): void => {
  trackFullStoryEvent(MARK_PANEL_EVENT.responseFailed, {
    surface,
    requestType,
    errorType,
  });
};
